import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import LayoutContainer from 'components/Common/LayoutContainer'
import {
  arrayToObject,
  EmptyArray,
  groupBy,
  sortBy,
  sum,
  without,
} from 'lib/array/arrayUtils'
import {
  AnythingCategory,
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_FOOD_AND_DRINK_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
  EXP_CATEGORY_GUIDED_TOURS_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_SHOWS_AND_EVENTS_ID,
  EXP_CATEGORY_TICKETS_AND_PASSES_ID,
  EXP_CATEGORY_TOP_ACTIVITIES_ID,
  EXP_CATEGORY_WELLNESS_AND_SPA_ID,
} from 'constants/experience'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import ToggleButton from 'LuxKit/ToggleButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useToggle from 'hooks/useToggle'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineWellnessIcon from 'LuxKit/Icons/line/LineWellnessIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineTheatreIcon from 'LuxKit/Icons/line/LineTheatreIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import BodyText from 'LuxKit/Typography/BodyText'

const CATEGORY_ICON_MAP: Record<number, React.ReactElement> = {
  [EXP_CATEGORY_FOOD_AND_DRINK_ID]: <LineRestaurantIcon />,
  [EXP_CATEGORY_TOP_ACTIVITIES_ID]: <LineHotAirBalloonIcon />,
  [EXP_CATEGORY_TICKETS_AND_PASSES_ID]: <LineTicketIcon />,
  [EXP_CATEGORY_SHOWS_AND_EVENTS_ID]: <LineTheatreIcon />,
  [EXP_CATEGORY_WELLNESS_AND_SPA_ID]: <LineWellnessIcon />,
  [EXP_CATEGORY_DAY_TRIPS_ID]: <LinePlaneDepartureIcon />,
  [EXP_CATEGORY_LUX_EXCLUSIVE_ID]: <LineStarIcon />,
  [EXP_CATEGORY_GUIDED_TOURS_ID]: <LineGuidedtoursIcon />,
  [EXP_CATEGORY_GIFT_INSPIRATION]: <LineGiftIcon />,
  [EXP_CATEGORY_LUX_PLUS_ID]: <LineAwardIcon />,
}

const DEFAULT_CATEGORY_ICON = <LineStarIcon />

const CategoryToggleButton = styled(ToggleButton)`
  &.kind-secondary {
    border-color: transparent;
    box-shadow: inset 0 0 0 1px
      ${(props) => props.theme.palette.neutral.default.six};

    &.is-selected {
      box-shadow: inset 0 0 0 1px
        ${(props) => props.theme.palette.neutral.default.one};
    }
  }
`

const FiltersContainer = styled.div`
  border-top: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  padding-top: ${rem(12)};
  padding-bottom: ${rem(4)};
`

const FiltersRow = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
`

const FullWidthFiltersRow = styled(FiltersRow)`
  max-width: 100vw !important;
  padding-left: ${rem(16)} !important;
  padding-right: ${rem(16)} !important;
`

const ListViewFiltersRow = styled(FiltersRow)`
  @media (min-width: ${rem(1600)}) {
    padding-left: ${rem(294)} !important;
  }
`

interface CategoryFilterItem {
  name: string;
  count: number;
  value: number;
  checked: boolean;
  hidden: boolean;
  disabled: boolean;
}

interface CategoryItem {
  category: App.ExperienceItemCategory;
  count: number;
  items: Array<CategoryFilterItem>;
  title: string;
}

const CATEGORY_DISPLAY_ORDER = [
  EXP_CATEGORY_TOP_ACTIVITIES_ID,
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_TICKETS_AND_PASSES_ID,
  EXP_CATEGORY_FOOD_AND_DRINK_ID,
  EXP_CATEGORY_GUIDED_TOURS_ID,
  EXP_CATEGORY_WELLNESS_AND_SPA_ID,
  EXP_CATEGORY_SHOWS_AND_EVENTS_ID,
]

export function sortCategoryItemsWithTopActivitiesFirst(
  a: CategoryItem,
  b: CategoryItem,
): number {
  const aIndex = CATEGORY_DISPLAY_ORDER.indexOf(a.category.id)
  const bIndex = CATEGORY_DISPLAY_ORDER.indexOf(b.category.id)
  const aOrder = aIndex === -1 ? CATEGORY_DISPLAY_ORDER.length : aIndex
  const bOrder = bIndex === -1 ? CATEGORY_DISPLAY_ORDER.length : bIndex
  return aOrder - bOrder
}

interface CategoryFilterButtonProps {
  categoryItem: CategoryItem;
  onApplyFilters: (
    categoryItem: CategoryItem,
    selectedIds: Set<number>,
  ) => void;
  onResetCategory: (item: CategoryItem) => void;
}

function CategoryFilterButton({
  categoryItem,
  onApplyFilters,
  onResetCategory,
}: CategoryFilterButtonProps) {
  const intl = useIntl()
  const [isOpen, toggleOpen, , closeDropdown] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [pendingChecked, setPendingChecked] = useState<Set<number>>(
    () =>
      new Set(
        categoryItem.items
          .filter((filterItem) => filterItem.checked)
          .map((filterItem) => filterItem.value),
      ),
  )

  useEffect(() => {
    if (isOpen) {
      setPendingChecked(
        new Set(
          categoryItem.items
            .filter((filterItem) => filterItem.checked)
            .map((filterItem) => filterItem.value),
        ),
      )
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const selectedCount = categoryItem.items.filter(
    (item) => item.checked,
  ).length
  const categoryName = categoryItem.title
  const icon =
    CATEGORY_ICON_MAP[categoryItem.category.id] ?? DEFAULT_CATEGORY_ICON

  const handleToggle = useCallback((value: number) => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const onReset = useCallback(() => {
    onResetCategory(categoryItem)
    closeDropdown()
  }, [categoryItem, onResetCategory, closeDropdown])

  const onApply = useCallback(() => {
    onApplyFilters(categoryItem, pendingChecked)
    closeDropdown()
  }, [categoryItem, pendingChecked, onApplyFilters, closeDropdown])

  const visibleItems = useMemo(
    () => categoryItem.items.filter((item) => !item.hidden && !item.disabled),
    [categoryItem.items],
  )

  const allSelected = useMemo(
    () =>
      visibleItems.length > 0 &&
      visibleItems.every((item) => pendingChecked.has(item.value)),
    [visibleItems, pendingChecked],
  )

  const handleSelectAll = useCallback(() => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (visibleItems.every((item) => next.has(item.value))) {
        visibleItems.forEach((item) => next.delete(item.value))
      } else {
        visibleItems.forEach((item) => next.add(item.value))
      }
      return next
    })
  }, [visibleItems])

  return (
    <div>
      <CategoryToggleButton
        ref={triggerRef}
        kind="secondary"
        orientation="horizontal"
        isSelected={!!selectedCount}
        title={categoryName}
        startIcon={icon}
        endIcon={<LineAngleDownIcon />}
        onClick={toggleOpen}
      />
      <DropdownList
        size="S"
        width="max"
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        open={isOpen}
        placement="bottom-start"
        onClose={closeDropdown}
        title={categoryName}
        hasDismissButton
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'XGAxBfjicX0x40wpm7Xt',
            defaultMessage: 'Apply',
            description:
              'Button text to apply sort and filter selections in experience search',
          }),
          onClick: onApply,
          style: { flex: 1 },
        }}
        secondaryActionProps={{
          kind: 'tertiary',
          children: intl.formatMessage({
            id: '61b-YHMXB-JsuWKpgENc',
            defaultMessage: 'Reset',
            description:
              'Button text to reset filter selections in experience search map filter dropdown',
          }),
          onClick: onReset,
        }}
      >
        {categoryItem.items.length === 0 && (
          <BodyText variant="medium" style={{ paddingLeft: rem(16) }}>
            {intl.formatMessage({
              id: 'dagJf2WFiyWNfetEbv29',
              defaultMessage: 'No available categories',
              description:
                'Empty state message when no categories are available for filtering in experience search map',
            })}
          </BodyText>
        )}
        {categoryItem.items.length > 0 && (
          <CheckboxInputListItem
            inputPlacement="start"
            checked={allSelected}
            onChange={handleSelectAll}
            disabled={visibleItems.length === 0}
            title={intl.formatMessage({
              id: 'GC6-4lugK4uyh65fIwhd',
              defaultMessage: 'Select all',
              description:
                'Option to select all subcategories within a category filter',
            })}
            size="medium"
          />
        )}
        {categoryItem.items.map((item) => (
          <CheckboxInputListItem
            key={item.value}
            inputPlacement="start"
            checked={pendingChecked.has(item.value)}
            onChange={() => handleToggle(item.value)}
            title={item.name}
            size="medium"
            disabled={item.disabled}
          >
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'AK3J75yI0gs1r7X6v_It',
                  defaultMessage: '({count})',
                  description:
                    'Count of matching experiences shown next to a category filter option',
                },
                { count: item.count },
              )}
            </BodyText>
          </CheckboxInputListItem>
        ))}
      </DropdownList>
    </div>
  )
}

interface MappedProps {
  categories: Array<App.ExperienceItemCategory>;
}

interface Props {
  place?: App.Place;
  isMapView?: boolean;
}

function ExperienceSearchCategoryFilters({
  place,
  isMapView,
  categories = EmptyArray,
}: Props & MappedProps) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()

  const { categories: categoriesFacets, luxPlusMemberPriceCount } =
    useMostPopulatedSearchExperienceFacets(place)

  const isGiftSelected = useMemo(
    () => searchFilters.featured?.includes('is-gift') ?? false,
    [searchFilters.featured],
  )

  const onToggleGiftInspiration = useCallback(() => {
    const next = isGiftSelected
      ? (searchFilters.featured ?? []).filter(
          (featured) => featured !== 'is-gift',
        )
      : [
          ...(searchFilters.featured ?? []),
          'is-gift' as Experiences.FeaturedFilter,
        ]
    setSearchFilters({
      ...searchFilters,
      featured: next.length > 0 ? next : undefined,
    })
  }, [isGiftSelected, searchFilters, setSearchFilters])

  const onApplyFilters = useCallback(
    (categoryItem: CategoryItem, selectedIds: Set<number>) => {
      const allItemValues = new Set(
        categoryItem.items.map((filterItem) => filterItem.value),
      )
      const currentCategories = searchFilters.categoryCodes ?? []
      const otherCategories = currentCategories.filter(
        (categoryCode) => !allItemValues.has(categoryCode),
      )
      setSearchFilters({
        ...searchFilters,
        categoryCodes: [...otherCategories, ...selectedIds],
      })
    },
    [searchFilters, setSearchFilters],
  )

  const EXCLUDED_CATEGORY_IDS = useMemo(
    () =>
      new Set([
        AnythingCategory.id,
        EXP_CATEGORY_GIFT_INSPIRATION,
        EXP_CATEGORY_LUX_EXCLUSIVE_ID,
        EXP_CATEGORY_LUX_PLUS_ID,
      ]),
    [],
  )

  const categoryItems = useMemo((): Array<CategoryItem> => {
    const categoriesWithoutAnything = categories.filter(
      (cat) => !EXCLUDED_CATEGORY_IDS.has(cat.id),
    )
    const childCategoriesByParent = groupBy(
      categoriesWithoutAnything,
      (cat) => cat.parentId,
    )
    const facetsById = arrayToObject(
      categoriesFacets,
      (facet) => facet.categoryId,
    )
    const topLevelCategories = categoriesWithoutAnything.filter(
      (cat) => cat.level === 'parent',
    )
    const selectedCategories = searchFilters.categoryCodes ?? []

    return topLevelCategories
      .map((category) => {
        const childCategories = sortBy(
          childCategoriesByParent.get(category.id) ?? [],
          (cat) => facetsById[cat.id]?.count ?? 0,
          'desc',
        )
        const total = sum(
          childCategories,
          (cat) => facetsById[cat.id]?.count ?? 0,
        )

        return {
          category,
          title: category.name,
          count: total,
          items: childCategories.map((cat): CategoryFilterItem => {
            const count = facetsById[cat.id]?.count ?? 0

            return {
              name: cat.name,
              count,
              hidden: false,
              disabled: count === 0,
              value: cat.id,
              checked:
                selectedCategories.includes(cat.id) ||
                selectedCategories.includes(cat.parentId ?? -1),
            }
          }),
        }
      })
      .sort(sortCategoryItemsWithTopActivitiesFirst)
  }, [
    categories,
    EXCLUDED_CATEGORY_IDS,
    categoriesFacets,
    searchFilters.categoryCodes,
  ])

  const isLuxPlusSelected = useMemo(
    () => !!searchFilters.luxPlusFeatures?.length,
    [searchFilters.luxPlusFeatures],
  )

  const isLuxExclusiveSelected = useMemo(
    () => searchFilters.leExclusive ?? false,
    [searchFilters.leExclusive],
  )

  const onToggleLuxPlus = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      luxPlusFeatures: isLuxPlusSelected ? undefined : ['Member prices'],
    })
  }, [isLuxPlusSelected, searchFilters, setSearchFilters])

  const onToggleLuxExclusive = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      leExclusive: isLuxExclusiveSelected ? undefined : true,
    })
  }, [isLuxExclusiveSelected, searchFilters, setSearchFilters])

  const onResetCategory = useCallback(
    (item: CategoryItem) => {
      const selectedCategories = searchFilters.categoryCodes ?? []
      const nextCategories = without(
        selectedCategories,
        item.category.id,
        ...item.items.map((filterItem) => filterItem.value),
      )
      setSearchFilters({
        ...searchFilters,
        categoryCodes: nextCategories,
      })
    },
    [searchFilters, setSearchFilters],
  )

  const mapOnlyButtons = isMapView ? (
    <>
      <CategoryToggleButton
        kind="secondary"
        orientation="horizontal"
        isSelected={isLuxPlusSelected}
        title={intl.formatMessage({
          id: 'VXzlXKhexN9O3AS513vs',
          defaultMessage: 'Lux Plus',
          description:
            'Toggle button to filter experiences by Lux Plus on the map search page',
        })}
        startIcon={<LineAwardIcon />}
        onClick={onToggleLuxPlus}
        disabled={luxPlusMemberPriceCount === 0}
      />
      <CategoryToggleButton
        kind="secondary"
        orientation="horizontal"
        isSelected={isLuxExclusiveSelected}
        title={intl.formatMessage({
          id: 'h0N4nOkFi58_HIjKkRRL',
          defaultMessage: 'Lux exclusive',
          description:
            'Toggle button to filter experiences by Lux Exclusive on the map search page',
        })}
        startIcon={<LineStarIcon />}
        onClick={onToggleLuxExclusive}
      />
      <CategoryToggleButton
        kind="secondary"
        orientation="horizontal"
        isSelected={isGiftSelected}
        title={intl.formatMessage({
          id: 'KEI-z8gsP1hzdvMir72B',
          defaultMessage: 'Gift inspiration',
          description:
            'Toggle button to filter experiences by gift inspiration on the map search page',
        })}
        startIcon={<LineGiftIcon />}
        onClick={onToggleGiftInspiration}
      />
    </>
  ) : null

  if (categoryItems.length === 0 && !mapOnlyButtons) {
    return null
  }

  const filterButtons = categoryItems.map((item) => (
    <CategoryFilterButton
      key={item.category.id}
      categoryItem={item}
      onApplyFilters={onApplyFilters}
      onResetCategory={onResetCategory}
    />
  ))

  return (
    <FiltersContainer>
      {isMapView && (
        <FullWidthFiltersRow>
          {mapOnlyButtons}
          {filterButtons}
        </FullWidthFiltersRow>
      )}
      {!isMapView && (
        <ListViewFiltersRow variant="extraLarge">
          {filterButtons}
        </ListViewFiltersRow>
      )}
    </FiltersContainer>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperienceSearchCategoryFilters)
